import get from 'lodash/get';
import isEqual from 'lodash/isEqual';
import isObject from 'lodash/isObject';
import startCase from 'lodash/startCase';
import toLower from 'lodash/toLower';
import transform from 'lodash/transform';

import { pagination } from './constants';
import {
  formatDate,
  formatDateTime,
  formatLongDate,
  formatMediumDate,
  formatMediumDateMaxYear,
  formatMonthYearDate,
  formatTableDateTime,
  formatTime,
} from './dateFormatters';

export {
  formatDate,
  formatDateTime,
  formatLongDate,
  formatMediumDate,
  formatMediumDateMaxYear,
  formatMonthYearDate,
  formatTime,
  formatTableDateTime,
};

// Formik field validations

export const required = (value, options) => {
  if (!value || value === 'none' || (typeof value === 'string' && value.trim() === '')) {
    return 'Required';
  }
  if (options) {
    if (options.minLength && value.length < options.minLength) {
      return `Minimum of ${options.minLength} characters`;
    }
    if (options.maxLength && value.length > options.maxLength) {
      return `Maximum of ${options.maxLength} characters`;
    }
  }
  return null;
};

// formatting

export const formatCurrency = (amount) => {
  const value = Number(amount);

  return typeof value === 'number' && (amount || amount === 0)
    ? value.toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
      })
    : '-';
};

export const getFullName = (obj) => {
  if (!obj) {
    return '';
  }
  const firstName = obj?.first_name ?? '';
  const lastName = obj?.last_name ?? '';
  return [firstName, lastName].filter((x) => !!x).join(' ');
};

export const displayName = (business, applicant) => {
  if (business) {
    return get(business, 'name');
  }
  return getFullName(applicant);
};

// Converts strings to all lower case before capitalizing first characters of each word.
export const toUpperCase = (str) => {
  return startCase(toLower(str));
};

export const difference = (object, base) => {
  return transform(object, (result, value, key) => {
    if (!isEqual(value, base[key])) {
      // eslint-disable-next-line no-param-reassign
      result[key] =
        isObject(value) && isObject(base[key]) ? difference(value, base[key]) : value;
    }
  });
};

export const last4NumMask = (number) => {
  const last4Num = number && number.length >= 4 && ` *${number.slice(-4)}`;

  if (!last4Num) {
    return '';
  }
  return last4Num;
};

// Converts hex value to rgba and allows opacity values
export const hex2rgba = (hex, alpha = 1) => {
  const [r, g, b] = hex.match(/\w\w/g).map((x) => parseInt(x, 16));
  return `rgba(${r},${g},${b},${alpha})`;
};

export const maskAccountNumber = (accountNumber) => {
  return accountNumber && accountNumber.length >= 4 && ` ****${accountNumber.slice(-4)}`;
};

export const hashCode = (s) =>
  // eslint-disable-next-line no-bitwise
  Math.abs(s.split('').reduce((a, b) => ((a << 5) - a + b.charCodeAt(0)) | 0, 0));

export const skeletonWidth = (id, lowerBound, upperBound) => {
  const widthDifference = upperBound - lowerBound;
  const hash = hashCode(id);
  return ((hash * 1049) % widthDifference) + lowerBound;
};

export async function copyToClipboard(value) {
  try {
    await navigator?.clipboard?.writeText(value);
  } catch {} // eslint-disable-line no-empty
}

export const getSearchParams = (request) => {
  const { searchParams } = new URL(request.url);
  if (!searchParams.get('page_size')) {
    searchParams.set('page_size', pagination.pageSize.toString());
  }

  return searchParams;
};

export const getOrgName = (orgs, orgId) => orgs[orgId]?.name || orgId;

export const downloadText = (text) => {
  const element = document.createElement('a');
  const file = new Blob([text], { type: 'text/plain' });
  element.href = URL.createObjectURL(file);
  element.download = 'key.txt';
  document.body.appendChild(element); // Required for this to work in FireFox
  element.click();
};

export const isLedger = (bankId) => {
  return bankId === 'bank_treasuryprime';
};

export const maskTin = (type, tin) => {
  if (!tin) {
    return '';
  }

  return type === 'business' ? `**-**${tin.slice(-4)}` : `***-**-${tin.slice(-4)}`;
};

// temporary until Launch Darkly is added
export const getHidePagesFlag = () => true;

export const getHideAccountsSecondaryItemsFlag = () => true;
export const getHideCompliancePageFlag = () => true;
export const getHideDashboardPageFlag = () => true;
